<template>
  <Dialog
    :is-open="isOpen"
    title="AI Summary"
    width="700px"
    @close="$emit('close')"
  >
    <div :class="$style.container">
      <!-- Loading State -->
      <div v-if="isLoading" :class="$style.loading">
        <div :class="$style.spinner"></div>
        <p>Generating AI summary...</p>
      </div>

      <!-- Error State -->
      <div v-else-if="error" :class="$style.error">
        <WarningIcon :size="24" />
        <h3>Failed to generate summary</h3>
        <p>{{ error }}</p>
        <button :class="$style.retryButton" @click="generateSummary">
          Try Again
        </button>
      </div>

      <!-- Summary Content -->
      <div v-else-if="summary" :class="$style.summary">
        <!-- Refresh Button -->
        <div :class="$style.refreshSection">
          <button
            :class="$style.refreshButton"
            @click="generateSummary"
            :disabled="isLoading"
          >
            <RestoreIcon :size="16" />
            {{ isLoading ? "Refreshing..." : "Refresh Summary" }}
          </button>
        </div>

        <!-- Debug Info (Development Only) -->
        <div
          v-if="isDevelopment"
          :class="$style.debugSection"
          style="
            background: rgba(255, 0, 0, 0.1);
            padding: 10px;
            margin-bottom: 20px;
          "
        >
          <h4>Debug Info:</h4>
          <p>Executive Summary: "{{ summary.executiveSummary }}"</p>
          <p>Key Insights Count: {{ summary.keyInsights?.length || 0 }}</p>
          <p>
            Areas for Improvement Count:
            {{ summary.areasForImprovement?.length || 0 }}
          </p>
          <p>
            Positive Patterns Count: {{ summary.positivePatterns?.length || 0 }}
          </p>
          <p>Next Steps Count: {{ summary.nextSteps?.length || 0 }}</p>
        </div>

        <!-- Executive Summary -->
        <div :class="$style.section">
          <h3 :class="$style.sectionTitle">Executive Summary</h3>
          <p :class="$style.executiveSummary">{{ summary.executiveSummary }}</p>
        </div>

        <!-- Key Insights -->
        <div :class="$style.section">
          <h3 :class="$style.sectionTitle">Key Insights</h3>
          <ul :class="$style.insightsList">
            <li
              v-for="(insight, index) in summary.keyInsights"
              :key="index"
              :class="$style.insightItem"
            >
              {{ insight }}
            </li>
          </ul>
        </div>

        <!-- Areas for Improvement -->
        <div :class="$style.section">
          <h3 :class="$style.sectionTitle">Areas for Improvement</h3>
          <ul :class="$style.improvementsList">
            <li
              v-for="(improvement, index) in summary.areasForImprovement"
              :key="index"
              :class="$style.improvementItem"
            >
              {{ improvement }}
            </li>
          </ul>
        </div>

        <!-- Positive Patterns -->
        <div :class="$style.section">
          <h3 :class="$style.sectionTitle">Positive Patterns</h3>
          <ul :class="$style.patternsList">
            <li
              v-for="(pattern, index) in summary.positivePatterns"
              :key="index"
              :class="$style.patternItem"
            >
              {{ pattern }}
            </li>
          </ul>
        </div>

        <!-- Next Steps -->
        <div :class="$style.section">
          <h3 :class="$style.sectionTitle">Next Steps</h3>
          <ul :class="$style.stepsList">
            <li
              v-for="(step, index) in summary.nextSteps"
              :key="index"
              :class="$style.stepItem"
            >
              {{ step }}
            </li>
          </ul>
        </div>
      </div>

      <!-- Generate Button or No API Key State -->
      <div
        v-else-if="hasApiKey && !summary && !isLoading"
        :class="$style.generateSection"
      >
        <p :class="$style.description">
          Generate an AI-powered summary of your time tracking data with
          personalized insights and recommendations.
        </p>
        <button :class="$style.generateButton" @click="generateSummary">
          Generate Summary
        </button>
      </div>

      <!-- No API Key State -->
      <div v-else :class="$style.noApiKeySection">
        <div :class="$style.noApiKeyIcon">🔑</div>
        <h3 :class="$style.noApiKeyTitle">API Key Required</h3>
        <p :class="$style.noApiKeyDescription">
          To generate AI summaries, you need to configure your Gemini API key.
        </p>
        <button :class="$style.configureButton" @click="openSettings">
          Configure API Key
        </button>
        <p :class="$style.noApiKeyHelp">
          Don't have an API key?
          <a
            href="https://aistudio.google.com/app/apikey"
            target="_blank"
            rel="noopener noreferrer"
            :class="$style.helpLink"
          >
            Get one for free from Google AI Studio
          </a>
        </p>
      </div>
    </div>
  </Dialog>
</template>

<script setup lang="ts">
import { ref, onMounted, watch, computed } from "vue";
import Dialog from "./Dialog.vue";
import { WarningIcon, RestoreIcon } from "./icons";
import { geminiService, type SummaryRequest } from "../services/geminiService";
import {
  analyticsService,
  type FilterSettings,
} from "../services/analyticsService";
import type { Task } from "../types/task";

const props = defineProps<{
  isOpen: boolean;
  tasks: Task[];
  filters: FilterSettings;
}>();

defineEmits<{
  (e: "close"): void;
}>();

const isLoading = ref(false);
const error = ref<string | null>(null);
const summary = ref<any>(null);
const hasApiKey = ref(false);

const isDevelopment = computed(() => {
  return import.meta.env.DEV || window.location.hostname === "localhost";
});

// Get Gemini API key from environment or user settings
const getGeminiApiKey = (): string => {
  // You can store this in localStorage, environment variables, or user settings
  const storedKey = localStorage.getItem("gemini-api-key");
  if (storedKey) return storedKey;

  // Fallback to environment variable (for development)
  return import.meta.env.VITE_GEMINI_API_KEY || "";
};

// Check if API key is configured
const checkApiKey = () => {
  const apiKey = getGeminiApiKey();
  hasApiKey.value = apiKey.length > 0;
};

// Open settings dialog
const openSettings = () => {
  // Emit event to parent to open settings
  // This will be handled by the parent component
  window.dispatchEvent(new CustomEvent("open-gemini-settings"));
};

const generateSummary = async () => {
  if (isLoading.value) return;

  const apiKey = getGeminiApiKey();
  if (!apiKey) {
    error.value =
      "Gemini API key not found. Please configure your API key in settings.";
    return;
  }

  isLoading.value = true;
  error.value = null;

  try {
    // Calculate analytics data
    const insights = analyticsService.calculateTimeManagementInsights(
      props.tasks,
      props.filters
    );
    const completion = analyticsService.calculateCompletionMetrics(
      props.tasks,
      props.filters
    );
    const topicAnalysis = analyticsService.calculateTopicAnalysis(
      props.tasks,
      props.filters
    );
    const overrunAnalysis = analyticsService.calculateOverrunAnalysis(
      props.tasks,
      props.filters
    );

    // Prepare summary request
    const summaryData: SummaryRequest = {
      timePeriod: getTimePeriodLabel(props.filters.selectedPeriod),
      timeManagementInsights: insights,
      completionMetrics: completion,
      topicAnalysis: topicAnalysis,
      overrunAnalysis: overrunAnalysis,
    };

    // Generate summary using Gemini
    const result = await geminiService.generateSummary(summaryData, {
      apiKey,
      model: "gemini-2.0-flash-exp",
      maxTokens: 1000,
      temperature: 0.7,
    });

    summary.value = result;
    if (isDevelopment.value) {
      console.log("SummaryDialog - summary.value updated:", summary.value);
      console.log(
        "SummaryDialog - executiveSummary:",
        summary.value.executiveSummary
      );
      console.log("SummaryDialog - keyInsights:", summary.value.keyInsights);
    }
  } catch (err) {
    console.error("Error generating summary:", err);
    error.value =
      err instanceof Error ? err.message : "Failed to generate summary";
  } finally {
    isLoading.value = false;
  }
};

const getTimePeriodLabel = (period: string): string => {
  const labels: Record<string, string> = {
    ALL: "All Time",
    TODAY: "Today",
    WEEK: "This Week",
    MONTH: "This Month",
    YEAR: "This Year",
    CUSTOM: "Custom Period",
  };
  return labels[period] || period;
};

// Auto-generate summary when dialog opens
onMounted(() => {
  checkApiKey();
  if (props.isOpen && !summary.value && !isLoading.value && hasApiKey.value) {
    generateSummary();
  }
});

// Watch for dialog opening and API key changes
watch(
  () => props.isOpen,
  (newValue) => {
    if (newValue) {
      checkApiKey();
      // Auto-generate summary when dialog opens if we have an API key and no existing summary
      if (hasApiKey.value && !summary.value && !isLoading.value) {
        generateSummary();
      }
    }
  }
);

// Watch for API key changes
watch(
  () => hasApiKey.value,
  (newValue) => {
    if (newValue && props.isOpen && !summary.value && !isLoading.value) {
      generateSummary();
    }
  }
);
</script>

<style module>
.container {
  padding: 20px;
  min-height: 400px;
}

.loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  gap: 16px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 4px solid rgba(255, 255, 255, 0.1);
  border-left: 4px solid #74b9ff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  gap: 16px;
  text-align: center;
}

.error h3 {
  margin: 0;
  color: #e74c3c;
}

.retryButton {
  background: #74b9ff;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

.retryButton:hover {
  background: #5a9bd8;
}

.generateSection {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  gap: 20px;
  text-align: center;
}

.description {
  color: rgba(255, 255, 255, 0.8);
  max-width: 400px;
  line-height: 1.5;
}

.generateButton {
  background: linear-gradient(135deg, #74b9ff, #0984e3);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.2s ease;
}

.generateButton:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(116, 185, 255, 0.3);
}

.noApiKeySection {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  gap: 16px;
  text-align: center;
}

.noApiKeyIcon {
  font-size: 48px;
  margin-bottom: 8px;
}

.noApiKeyTitle {
  margin: 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: white;
}

.noApiKeyDescription {
  color: rgba(255, 255, 255, 0.8);
  max-width: 400px;
  line-height: 1.5;
  margin: 0;
}

.configureButton {
  background: linear-gradient(135deg, #74b9ff, #0984e3);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  transition: all 0.2s ease;
  margin-top: 8px;
}

.configureButton:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(116, 185, 255, 0.3);
}

.noApiKeyHelp {
  color: rgba(255, 255, 255, 0.6);
  font-size: 14px;
  margin: 0;
  margin-top: 8px;
}

.helpLink {
  color: #74b9ff;
  text-decoration: none;
}

.helpLink:hover {
  text-decoration: underline;
}

.summary {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.section {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 20px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.sectionTitle {
  margin: 0 0 12px 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: white;
  display: flex;
  align-items: center;
  gap: 8px;
}

.executiveSummary {
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.6;
  margin: 0;
}

.insightsList,
.improvementsList,
.patternsList,
.stepsList {
  margin: 0;
  padding-left: 20px;
  color: rgba(255, 255, 255, 0.9);
}

.insightItem,
.improvementItem,
.patternItem,
.stepItem {
  margin-bottom: 8px;
  line-height: 1.5;
}

.insightItem::marker,
.patternItem::marker {
  color: #74b9ff;
}

.improvementItem::marker {
  color: #fdcb6e;
}

.stepItem::marker {
  color: #00b894;
}

.refreshSection {
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.refreshButton {
  background: linear-gradient(135deg, #74b9ff, #0984e3);
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s ease;
}

.refreshButton:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(116, 185, 255, 0.3);
}

.refreshButton:disabled {
  background: rgba(255, 255, 255, 0.1);
  cursor: not-allowed;
  color: rgba(255, 255, 255, 0.4);
  transform: none;
  box-shadow: none;
}

.refreshButton:disabled:hover {
  transform: none;
  box-shadow: none;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .container {
    padding: 15px;
  }

  .section {
    padding: 15px;
  }

  .generateSection {
    height: 250px;
  }

  .description {
    max-width: 300px;
  }
}
</style>
